import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_BASE_URL}/api/`;

export default function useBrandProfile() {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Get auth token from sessionStorage
    const getAuthToken = () => {
        return sessionStorage.getItem('authToken');
    };

    // Create axios instance with auth header
    const createAuthAxios = () => {
        const token = getAuthToken();
        if (!token) {
            throw new Error('No authentication token found');
        }

        return axios.create({
            baseURL: API_BASE_URL,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
    };

    const fetchProfile = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Check if token exists
            const token = getAuthToken();
            if (!token) {
                setError('Authentication required');
                setProfile(null);
                setLoading(false);
                return;
            }

            // Create authenticated axios instance
            const authAxios = createAuthAxios();

            // Make API call to get brand profile
            const response = await authAxios.get('/profile');

            if (response.data) {
                setProfile(response.data);
            } else {
                setProfile(null);
            }
        } catch (err) {
            console.error('Failed to fetch brand profile:', err);

            // Handle specific error cases
            if (err.response?.status === 401) {
                setError('Session expired. Please login again.');
                // Optionally clear the invalid token
                sessionStorage.removeItem('authToken');
            } else if (err.response?.status === 404) {
                setError('Profile not found');
            } else {
                setError(err.response?.data?.message || err.message || 'Failed to load profile');
            }
            setProfile(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    const updateProfile = useCallback((updatedProfile) => {
        setProfile(updatedProfile);
    }, []);

    const clearProfile = useCallback(() => {
        setProfile(null);
    }, []);

    // Helper function to check if user is authenticated
    const isAuthenticated = useCallback(() => {
        return !!getAuthToken();
    }, []);

    return {
        profile,
        loading,
        error,
        fetchProfile,
        updateProfile,
        clearProfile,
        isAuthenticated
    };
}